import {
  Mic,
  MicOff,
  Monitor,
  MonitorOff,
  MonitorUp,
  PhoneOff,
  Video,
  VideoOff,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { VyllageMeetingParticipant } from "./world-engine";

export type MeetingModeParticipant = VyllageMeetingParticipant & {
  stream: MediaStream | null;
  isSelf?: boolean;
  sharing?: boolean;
};

type MeetingModeOverlayProps = {
  open: boolean;
  roomName: string;
  personal?: boolean;
  live?: boolean;
  selfName: string;
  selfColor: string;
  selfStream: MediaStream | null;
  selfCamOn: boolean;
  selfMicOn: boolean;
  selfSharing: boolean;
  participants: MeetingModeParticipant[];
  mic: boolean;
  cam: boolean;
  onToggleMic: () => void;
  onToggleCam: () => void;
  onToggleShare: () => void;
  onStandUp: () => void;
  /** Keep meeting audio but show the map again (still seated). */
  onMinimize: () => void;
};

/**
 * Zoom-style full-bleed video grid while seated in a private room / office.
 * Streams are shared with the world engine cams (same MediaStream is fine).
 * When someone is screen-sharing, switches to a presenter layout (large share + strip).
 */
export function MeetingModeOverlay({
  open,
  roomName,
  personal,
  live,
  selfName,
  selfColor,
  selfStream,
  selfCamOn,
  selfMicOn,
  selfSharing,
  participants,
  mic,
  cam,
  onToggleMic,
  onToggleCam,
  onToggleShare,
  onStandUp,
  onMinimize,
}: MeetingModeOverlayProps) {
  const tiles = useMemo(() => {
    const self: MeetingModeParticipant = {
      id: "__self__",
      name: selfName,
      camOn: selfCamOn,
      micOn: selfMicOn,
      color: selfColor,
      stream: selfStream,
      isSelf: true,
      sharing: selfSharing,
    };
    return [self, ...participants];
  }, [selfName, selfColor, selfStream, selfCamOn, selfMicOn, selfSharing, participants]);

  const presenter = useMemo(() => tiles.find((t) => t.sharing) ?? null, [tiles]);
  const others = useMemo(
    () => (presenter ? tiles.filter((t) => t.id !== presenter.id) : tiles),
    [tiles, presenter],
  );

  const count = tiles.length;
  const gridClass =
    count <= 1
      ? "grid-cols-1"
      : count === 2
        ? "grid-cols-1 sm:grid-cols-2"
        : count <= 4
          ? "grid-cols-2"
          : count <= 6
            ? "grid-cols-2 lg:grid-cols-3"
            : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

  const someoneSharing = !!presenter;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onMinimize();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onMinimize]);

  if (!open) return null;

  return (
    <div
      className="absolute inset-0 z-[55] flex flex-col bg-[#0B1220]"
      role="dialog"
      aria-label={`Meeting in ${roomName}`}
      aria-modal="true"
    >
      {/* Header */}
      <div className="flex shrink-0 items-center gap-3 border-b border-white/10 bg-[#0A1426]/95 px-4 py-2.5">
        <span
          className={cn(
            "h-2 w-2 shrink-0 rounded-full",
            live ? "animate-pulse bg-[var(--vy-bad)]" : "bg-[var(--vy-accent)]",
          )}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-white">{roomName}</p>
          <p className="text-[11px] text-white/55">
            {someoneSharing
              ? `${presenter.name}${presenter.isSelf ? " (you)" : ""} is sharing · private audio`
              : live
                ? `${count} in room · private audio`
                : personal
                  ? "Your office · sit to host"
                  : "Waiting for others · private room"}
          </p>
        </div>
        <button
          type="button"
          onClick={onMinimize}
          className="flex cursor-pointer items-center gap-1.5 rounded-full border border-white/15 bg-white/8 px-3 py-1.5 text-[11px] font-semibold text-white/90 hover:bg-white/14"
          title="Show map while still seated"
        >
          <Monitor className="h-3.5 w-3.5" />
          Map
        </button>
      </div>

      {/* Video area */}
      {presenter ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2 p-3 sm:gap-3 sm:p-4">
          <div className="min-h-0 flex-[1_1_68%]">
            <VideoTile participant={presenter} large share />
          </div>
          {others.length > 0 ? (
            <div
              className={cn(
                "grid max-h-[28%] min-h-[88px] shrink-0 gap-2 sm:max-h-[32%]",
                others.length === 1
                  ? "grid-cols-1 sm:grid-cols-2 sm:max-w-md"
                  : others.length === 2
                    ? "grid-cols-2 sm:grid-cols-3"
                    : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4",
              )}
            >
              {others.map((t) => (
                <VideoTile key={t.id} participant={t} compact />
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className={cn("min-h-0 flex-1 grid gap-2 p-3 sm:gap-3 sm:p-4", gridClass)}>
          {tiles.map((t) => (
            <VideoTile key={t.id} participant={t} />
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex shrink-0 items-center justify-center gap-3 border-t border-white/10 bg-[#0A1426] px-4 py-3">
        <ControlBtn
          label={mic ? "Mute" : "Unmute"}
          onClick={onToggleMic}
          danger={!mic}
          className="min-w-[4.5rem]"
        >
          {mic ? (
            <Mic className="h-5 w-5" aria-hidden />
          ) : (
            <MicOff className="h-5 w-5" aria-hidden />
          )}
          <span className="text-[10px] font-bold tracking-wide">{mic ? "Mic" : "Muted"}</span>
        </ControlBtn>
        <ControlBtn
          label={cam ? "Stop video" : "Start video"}
          onClick={onToggleCam}
          danger={!cam}
          className="min-w-[4.5rem]"
        >
          {cam ? (
            <Video className="h-5 w-5" aria-hidden />
          ) : (
            <VideoOff className="h-5 w-5" aria-hidden />
          )}
          <span className="text-[10px] font-bold tracking-wide">{cam ? "Cam" : "Off"}</span>
        </ControlBtn>
        <ControlBtn
          label={selfSharing ? "Stop sharing" : "Share screen"}
          onClick={onToggleShare}
          danger={selfSharing}
          className={cn("min-w-[5rem]", selfSharing && "bg-[var(--vy-cta)] hover:bg-[#1a5fb0]")}
        >
          {selfSharing ? (
            <MonitorOff className="h-5 w-5" aria-hidden />
          ) : (
            <MonitorUp className="h-5 w-5" aria-hidden />
          )}
          <span className="text-[10px] font-bold tracking-wide">
            {selfSharing ? "Stop" : "Share"}
          </span>
        </ControlBtn>
        <ControlBtn
          label="Stand up and leave meeting view"
          onClick={onStandUp}
          danger
          className="min-w-[5.5rem] bg-[var(--vy-bad)] hover:bg-[#c23a33]"
        >
          <PhoneOff className="h-5 w-5" aria-hidden />
          <span className="text-[10px] font-bold tracking-wide">Stand up</span>
        </ControlBtn>
      </div>
    </div>
  );
}

function VideoTile({
  participant,
  large,
  compact,
  share,
}: {
  participant: MeetingModeParticipant;
  large?: boolean;
  compact?: boolean;
  share?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [trackEpoch, setTrackEpoch] = useState(0);

  // Re-evaluate when remote tracks arrive late or mute/unmute
  useEffect(() => {
    const stream = participant.stream;
    if (!stream) return;
    const bump = () => setTrackEpoch((n) => n + 1);
    stream.addEventListener("addtrack", bump);
    stream.addEventListener("removetrack", bump);
    const tracks = stream.getTracks();
    for (const t of tracks) {
      t.addEventListener("mute", bump);
      t.addEventListener("unmute", bump);
      t.addEventListener("ended", bump);
    }
    return () => {
      stream.removeEventListener("addtrack", bump);
      stream.removeEventListener("removetrack", bump);
      for (const t of tracks) {
        t.removeEventListener("mute", bump);
        t.removeEventListener("unmute", bump);
        t.removeEventListener("ended", bump);
      }
    };
  }, [participant.stream]);

  void trackEpoch;
  const isShare = share || !!participant.sharing;
  const hasVideo =
    (participant.camOn || isShare) &&
    !!participant.stream &&
    participant.stream.getVideoTracks().some((t) => t.enabled && t.readyState === "live");

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (hasVideo && participant.stream) {
      if (el.srcObject !== participant.stream) {
        el.srcObject = participant.stream;
      }
      void el.play().catch(() => {});
    } else {
      el.srcObject = null;
    }
  }, [hasVideo, participant.stream, trackEpoch]);

  const initials = participant.name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border border-white/10 bg-[#121c30] shadow-lg",
        large
          ? "h-full min-h-[200px]"
          : compact
            ? "min-h-[80px]"
            : "min-h-[140px] sm:min-h-[180px]",
      )}
    >
      <video
        ref={videoRef}
        className={cn(
          "absolute inset-0 h-full w-full",
          isShare ? "object-contain bg-black" : "object-cover",
          hasVideo ? "opacity-100" : "opacity-0",
        )}
        autoPlay
        playsInline
        // Always muted — SpatialMediaSession owns remote (and local monitor) audio.
        muted
      />
      {!hasVideo ? (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            background: `linear-gradient(145deg, ${shadeHex(participant.color, -40)}, ${participant.color}, ${shadeHex(participant.color, 30)})`,
          }}
        >
          <span
            className={cn(
              "flex items-center justify-center rounded-full bg-black/25 font-bold text-white ring-2 ring-white/25",
              compact ? "h-10 w-10 text-sm" : "h-16 w-16 text-xl",
            )}
            style={{ fontFamily: "var(--font-display)" }}
          >
            {initials || "?"}
          </span>
        </div>
      ) : null}
      <div
        className={cn(
          "absolute right-0 bottom-0 left-0 flex items-center gap-1.5 bg-gradient-to-t from-black/75 to-transparent",
          compact ? "px-2 pt-4 pb-1.5" : "px-3 pt-6 pb-2",
        )}
      >
        <span
          className={cn("truncate font-semibold text-white", compact ? "text-[10px]" : "text-xs")}
        >
          {participant.name}
          {participant.isSelf ? " (you)" : ""}
        </span>
        {isShare ? (
          <span className="rounded bg-[var(--vy-cta)]/90 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-white uppercase">
            sharing
          </span>
        ) : null}
        {!participant.micOn ? (
          <span
            className={cn(
              "rounded bg-black/40 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-white/80 uppercase",
              isShare ? "" : "ml-auto",
            )}
          >
            muted
          </span>
        ) : null}
      </div>
    </div>
  );
}

function ControlBtn({
  children,
  label,
  onClick,
  danger,
  className,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center gap-0.5 rounded-2xl px-4 py-2.5 text-white transition-colors",
        danger ? "bg-white/10 hover:bg-white/16" : "bg-white/12 hover:bg-white/18",
        className,
      )}
    >
      {children}
    </button>
  );
}

function shadeHex(hex: string, amt: number): string {
  const h = hex.replace("#", "");
  if (h.length !== 6) return hex;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return hex;
  const r = Math.max(0, Math.min(255, ((n >> 16) & 255) + amt));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 255) + amt));
  const b = Math.max(0, Math.min(255, (n & 255) + amt));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
